import React, { useState, useRef } from 'react';
import './LoopController.css';
import { LoopMain } from '../LoopMain/LoopMain';
import { LoopService } from '../../../Services/LoopService';
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Loop {
    id: number;
    audioBuffer: AudioBuffer;
    backgroundColor: string;
    analyser: AnalyserNode | null;

}

export function LoopController(): JSX.Element {
    const [loops, setLoops] = useState<Loop[]>([]);
    const [selectedLoops, setSelectedLoops] = useState<number[]>([]);
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const nextLoopIdRef = useRef<number>(1);
    const sourcesRef = useRef<{ [key: number]: AudioBufferSourceNode | null }>({});
    const audioContextRef = useRef(new (window.AudioContext || (window as any).webkitAudioContext)());

    const getRandomColor = (): string => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };
    
    // Handle the drag and drop functionality
    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setLoops((prevLoops) => {
                const oldIndex = prevLoops.findIndex((loop) => loop.id === active.id);
                const newIndex = prevLoops.findIndex((loop) => loop.id === over.id);
                return arrayMove(prevLoops, oldIndex, newIndex);
            });
        }
    };

    // Start recording a new loop
    const startRecording = async () => {
        setIsRecording(true);
        const recBtn = document.querySelector('.recBtn');
        if (recBtn) {
            recBtn.classList.add('recording-active');
        }
        try {
            const audioBuffer = await LoopService.startRecording();
            const newLoop: Loop = {
                id: nextLoopIdRef.current++,
                audioBuffer,
                backgroundColor: getRandomColor(),
                analyser: null,
            };
            setLoops((prevLoops) => [...prevLoops, newLoop]);
        } catch (error) {
            console.error("Error during recording:", error);
        }
    };

    // Stop recording
    const stopRecording = () => {
        LoopService.stopRecording();
        setIsRecording(false);
        const recBtn = document.querySelector('.recBtn');
        if (recBtn) {
            recBtn.classList.remove('recording-active');
        }
    };

    // Play a single loop and set up the analyser
    const playLoop = (loop: Loop) => {
        if (!sourcesRef.current[loop.id]) {
            const source = audioContextRef.current.createBufferSource();
            source.buffer = loop.audioBuffer;
            source.loop = true;
            source.connect(audioContextRef.current.destination);

            // Create an analyser node for this specific loop and connect it
            const analyserNode = audioContextRef.current.createAnalyser();
            analyserNode.fftSize = 2048;
            source.connect(analyserNode);
            analyserNode.connect(audioContextRef.current.destination);

            // Set analyser for visualizing the loop
            setLoops((prevLoops) =>
                prevLoops.map((l) =>
                    l.id === loop.id ? { ...l, analyser: analyserNode } : l
                )
            );

            source.start();
            sourcesRef.current[loop.id] = source;
            console.log(`Loop ${loop.id} is playing`);

            // Add 'playing-active' class to the loop button
            const loopBtn = document.querySelector(`.loopBtn[data-id="${loop.id}"]`);
            if (loopBtn) {
                loopBtn.classList.add('playing-active');
            }
        } else {
            console.log(`Loop ${loop.id} is already playing`);
        }
    };

    // Stop a single loop
    const stopLoop = (loop: Loop) => {
        const source = sourcesRef.current[loop.id];
        if (source) {
            source.stop();
            source.disconnect();
            sourcesRef.current[loop.id] = null;
            console.log(`Loop ${loop.id} stopped`);

            // Update the state to reflect the loop has stopped
            setLoops((prevLoops) =>
                prevLoops.map((l) =>
                    l.id === loop.id ? { ...l, analyser: null } : l
                )
            );

            // Remove 'playing-active' class from the loop button
            const loopBtn = document.querySelector(`.loopBtn[data-id="${loop.id}"]`);
            if (loopBtn) {
                loopBtn.classList.remove('playing-active');
            }
        }
    };

    // Play selected loops
    const playSelectedLoops = () => {
        selectedLoops.forEach(loopId => {
            const loop = loops.find(loop => loop.id === loopId);
            if (loop) {
                playLoop(loop);
            }
        });

        // Add 'playing-active' class to the selected button
        const selectedBtn = document.querySelector('.selectedBtn');
        if (selectedBtn) {
            selectedBtn.classList.add('playing-active');
        }

        setIsPlaying(true);
    };

    // Stop selected loops
    const stopSelectedLoops = () => {
        selectedLoops.forEach(loopId => {
            const loop = loops.find(loop => loop.id === loopId);
            if (loop) {
                stopLoop(loop);
            }
        });

        // Remove 'playing-active' class from the selected button
        const selectedBtn = document.querySelector('.selectedBtn');
        if (selectedBtn) {
            selectedBtn.classList.remove('playing-active');
        }

        setIsPlaying(false);
    };

    // Handle loop selection
    const toggleLoopSelection = (loopId: number) => {
        setSelectedLoops((prevSelectedLoops) =>
            prevSelectedLoops.includes(loopId)
                ? prevSelectedLoops.filter(id => id !== loopId)
                : [...prevSelectedLoops, loopId]
        );
    };
    const deleteSelectedLoops = () => {
        setLoops((prevLoops) =>
            prevLoops.filter((loop: Loop) => !selectedLoops.includes(loop.id))
        );
        // Clear the selected loops after deletion
        stopSelectedLoops()
        setSelectedLoops([]);
    };

    return (
        <div className="LoopController">

            <div className="LoopControls">
                {isRecording ? (
                    <div className='recBtn' onClick={stopRecording}>Stop Recording</div>
                ) : (
                    <div className='recBtn' onClick={startRecording}>Start Recording</div>
                )}

                {isPlaying ? (
                    <div className='selectedBtn' onClick={stopSelectedLoops}>Stop Selected Loops</div>
                ) : (
                    <div className='selectedBtn' onClick={playSelectedLoops}>Play <br /> Selected</div>
                )}
                <div className='deletSelected' onClick={deleteSelectedLoops}>Delete Selected</div>
            </div>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={loops} strategy={verticalListSortingStrategy}>
                    <div className="LoopList">
                        {loops.map((loop) => (
                            <div key={loop.id} className="LoopItem" style={{ backgroundColor: loop.backgroundColor }}>
                                <input
                                    type="checkbox"
                                    checked={selectedLoops.includes(loop.id)}
                                    onChange={() => toggleLoopSelection(loop.id)}
                                />
                                <label>Loop {loop.id}</label>
                                {sourcesRef.current[loop.id] ? (
                                    <div className='loopBtn' data-id={loop.id} onClick={() => stopLoop(loop)}>Stop <br /> Loop {loop.id}</div>
                                ) : (
                                    <div className='loopBtn' data-id={loop.id} onClick={() => playLoop(loop)}>Play <br /> Loop {loop.id}</div>
                                )}
                                <LoopMain analyser={loop.analyser} />
                            </div>
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}